import type { SceneObject } from "../../SceneHost";
import { placeHtmlObject } from "../htmlLayerObject";
import {
  isStickyObject,
  isTextObject,
  objectHref,
  textContent,
  textDisplayStyle,
} from "../../textObject";
import { objectRuns, runsToHtml } from "../../textRuns";

/**
 * Надписи и стикеры рисуются обычным HTML: Safari не применяет к HTML
 * внутри SVG место объекта. См. editor/render/htmlLayerObject.
 */

function applyInnerStyles(div: HTMLElement, obj: SceneObject, editing: boolean): void {
  const s = textDisplayStyle(obj);
  div.style.color = s.color;
  div.style.fontSize = `${s.fontSize}px`;
  div.style.fontFamily = s.fontFamily;
  div.style.fontWeight = s.fontWeight || "normal";
  div.style.fontStyle = s.fontStyle || "normal";
  div.style.textAlign = s.textAlign;
  div.style.lineHeight = String(s.lineHeight);
  div.style.width = "100%";
  div.style.height = "100%";
  div.style.boxSizing = "border-box";
  div.style.padding = isStickyObject(obj) ? "10px 12px" : "4px 6px";
  // Высоту надписи считает тот, кто её набирал. У другого человека браузер
  // переносит строки чуть иначе — строк становится больше, и хвост текста
  // срезался: приходилось вручную расширять рамку, чтобы его стало видно.
  // У надписи фона нет, поэтому пусть лучше выходит за рамку, чем пропадает.
  // У стикера фон есть — там текст по-прежнему держим внутри карточки.
  div.style.overflow = isStickyObject(obj) ? "hidden" : "visible";
  // normal + overflow-wrap: как в редакторе — иначе при выходе из правки «прыгают» буквы.
  div.style.wordBreak = "normal";
  div.style.overflowWrap = "break-word";
  div.style.whiteSpace = "pre-wrap";
  div.style.textDecoration = s.textDecoration || "none";
  // Фон снимают — его надо и стереть, а не только не ставить.
  div.style.background = s.background ?? "";
  if (isStickyObject(obj)) {
    div.style.borderRadius = "4px";
    div.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
  } else {
    div.style.borderRadius = s.background ? "6px" : "";
  }
  const isLink = Boolean(objectHref(obj)) && isTextObject(obj);
  if (editing) {
    div.style.visibility = "hidden";
  } else {
    div.style.visibility = "";
    // Часть текста может быть жирной/цветной — тогда рисуем куски,
    // иначе обычным текстом (так дешевле и так же, как раньше).
    const runs = objectRuns(obj);
    if (runs) {
      const html = runsToHtml(runs);
      if (div.dataset.runsHtml !== html) {
        div.innerHTML = html;
        div.dataset.runsHtml = html;
      }
    } else {
      if (div.dataset.runsHtml !== undefined) delete div.dataset.runsHtml;
      const next = textContent(obj);
      if (div.textContent !== next) div.textContent = next;
    }
  }
  div.classList.toggle("wb-scene-obj__inner--link", isLink);
  if (isLink) {
    const href = objectHref(obj);
    div.title = href ? `Открыть: ${href}` : "Открыть ссылку";
  } else {
    div.removeAttribute("title");
  }
}

export function syncTextLikeOnGroup(g: HTMLElement, obj: SceneObject, editing = false): void {
  if (!isTextObject(obj) && !isStickyObject(obj)) return;
  const w = typeof obj.w === "number" ? obj.w : isStickyObject(obj) ? 200 : 160;
  const h = typeof obj.h === "number" ? obj.h : isStickyObject(obj) ? 200 : 40;
  placeHtmlObject(g, obj, w, h);

  let div = g.querySelector("div") as HTMLDivElement | null;
  if (!div) {
    div = document.createElement("div");
    g.appendChild(div);
  }
  div.className = isStickyObject(obj)
    ? "wb-scene-obj__inner wb-scene-obj__inner--sticky"
    : "wb-scene-obj__inner wb-scene-obj__inner--text";
  applyInnerStyles(div, obj, editing);
}

export function renderTextObject(obj: SceneObject): HTMLElement | null {
  if (!isTextObject(obj)) return null;
  const g = document.createElement("div");
  g.setAttribute("class", "wb-scene-obj wb-scene-obj--text");
  g.setAttribute("data-object-id", obj.id);
  syncTextLikeOnGroup(g, obj);
  return g;
}

export function renderStickyObject(obj: SceneObject): HTMLElement | null {
  if (!isStickyObject(obj)) return null;
  const g = document.createElement("div");
  g.setAttribute("class", "wb-scene-obj wb-scene-obj--sticky");
  g.setAttribute("data-object-id", obj.id);
  syncTextLikeOnGroup(g, obj);
  return g;
}
